import { useCallback, useRef, useEffect } from 'react';
import { useUpdateBehaviourActionState } from '@/hooks/useUpdateBehaviourActionState';
import type { BehaviourAgentExperienceAction } from '@/types/ui-blocks';

/**
 * Interface for behaviour action state with known properties
 */
interface BehaviourActionState {
  entries_stored?: boolean;
  scheduled?: boolean;
  sent_to_slack?: boolean;
}

/**
 * In-memory cache of action IDs whose entries have been stored in this SPA session.
 *
 * Why this is needed: after the mutation persists `entries_stored: true` to the server,
 * the TanStack Query cache is invalidated. But the ChatContext session messages (which
 * carry `behaviour_action_state`) are NOT re-synced from the cache — the ChatContext
 * is a separate in-memory store. So when the user navigates away and back within the
 * same SPA session, the slot component receives stale `behaviour_action_state: {}`
 * from the ChatContext and would re-store entries.
 *
 * This Set acts as a client-side guard that survives component remounts within the
 * SPA session. On full page reload it's cleared, but by then the server returns the
 * correct `behaviour_action_state` in the chat history response.
 *
 * Exported for testing purposes only.
 */
export const storedActionIds = new Set<string>();

/**
 * Hook to manage persistent entry storage state across sessions.
 * Extracts common logic for checking if entries were already stored
 * and handling the store success callback to persist state.
 *
 * Handles the streaming timing gap: during streaming, chatHistoryId
 * (from CHAT_METADATA event) may arrive after entries have already
 * been stored. In that case, the mutation is deferred until
 * chatHistoryId becomes available.
 *
 * Used by content-outline-preview-slot and content-refresh-preview-slot
 * to avoid code duplication.
 *
 * @param action - The behaviour agent experience action
 * @param behaviourActionState - Map of behaviour action states keyed by action.id
 * @param chatHistoryId - The chat history ID (optional, may not be available during streaming)
 * @param workflowInstanceId - Workflow instance ID from ChatMetadataContext
 * @param workflowExecutionId - Workflow execution ID from ChatMetadataContext
 * @returns Object with alreadyStored flag and handleStoreSuccess callback
 */
export function usePersistentEntryStore(
  action: BehaviourAgentExperienceAction,
  behaviourActionState: Record<string, unknown> | undefined,
  chatHistoryId: string | undefined,
  workflowInstanceId: string | undefined,
  workflowExecutionId: string | undefined
) {
  // Check if entries were already stored — from server state OR in-memory SPA cache
  const actionState = action.id && behaviourActionState
    ? (behaviourActionState[action.id] as BehaviourActionState | undefined)
    : undefined;
  const alreadyStored =
    (actionState?.entries_stored ?? false) ||
    (action.id ? storedActionIds.has(action.id) : false);

  // Mutation to persist entries_stored state after successful store.
  // Retry with backoff: during streaming, the chat history row may not be committed yet
  // when CHAT_METADATA arrives (the row lives in the streaming transaction). Retrying
  // ensures we persist the state once the transaction commits.
  const storeMutation = useUpdateBehaviourActionState({
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(2000 * 2 ** attemptIndex, 10000),
  });

  // Track whether store succeeded but the mutation couldn't fire yet (chatHistoryId missing).
  // This handles the streaming timing gap where CHAT_METADATA arrives after the store completes.
  const needsPersistRef = useRef(false);

  // Callback to mark entries as stored in the backend
  const handleStoreSuccess = useCallback(() => {
    // Mark in SPA cache immediately — survives navigations within the same session
    if (action.id) {
      storedActionIds.add(action.id);
    }

    if (workflowInstanceId && workflowExecutionId && chatHistoryId && action.id) {
      storeMutation.mutate({
        workflowInstanceId,
        workflowExecutionId,
        chatHistoryId,
        behaviourActionStateId: action.id,
        state: { entries_stored: true },
      });
      needsPersistRef.current = false;
    } else {
      // chatHistoryId not available yet — defer until it arrives
      needsPersistRef.current = true;
    }
  }, [workflowInstanceId, workflowExecutionId, chatHistoryId, action.id, storeMutation]);

  // Deferred persist: when chatHistoryId becomes available after store already succeeded
  useEffect(() => {
    if (
      needsPersistRef.current &&
      workflowInstanceId &&
      workflowExecutionId &&
      chatHistoryId &&
      action.id
    ) {
      storeMutation.mutate({
        workflowInstanceId,
        workflowExecutionId,
        chatHistoryId,
        behaviourActionStateId: action.id,
        state: { entries_stored: true },
      });
      needsPersistRef.current = false;
    }
  }, [chatHistoryId, workflowInstanceId, workflowExecutionId, action.id, storeMutation]);

  return { alreadyStored, handleStoreSuccess };
}
